/**
 * Redux duck for the candidate interview room.
 *
 * Holds the room-connection state plus the TWO failure-signal states as SEPARATE reducer keys —
 * `voicePipelineStatus` (the worker's `skillfit.voice_pipeline_status`, STT/TTS) and `conductingStatus`
 * (`skillfit.conducting_status`, LLM) — so the two are never conflated. A third
 * key, `questioningActive`, is driven by `skillfit.conducting_state` for the proctor view's
 * mute-state label.
 */

import { storableError } from '../../util/errors';
import { SESSION_END_STATES } from '../../util/aiInterviewSessionEndStates';
import {
  joinSkillFitInterviewRoom,
  resumeSkillFitInterview,
  fetchSkillFitSessionStatus,
  retrySkillFitInterviewer,
  fetchSkillFitInterviewRoomDisplay,
} from '../../util/api';

// LiveKit reliable-data topics this room subscribes to (must match the worker's emitters).
export const VOICE_PIPELINE_TOPIC = 'skillfit.voice_pipeline_status';
export const CONDUCTING_STATUS_TOPIC = 'skillfit.conducting_status';
export const CONDUCTING_STATE_TOPIC = 'skillfit.conducting_state';
// The time-budget pacing signal — a DISTINCT topic from the two failure signals and the
// conducting-state bookend, so the timing domain is never conflated with them.
export const TIME_BUDGET_TOPIC = 'skillfit.time_budget_status';
/*
 * The candidate's OUTBOUND typed turn, on its own NEW topic.
 *
 * Deliberately not a widened payload on any `skillfit.*` status topic above: three worker suites
 * assert those topics carry no transcript content, and a status topic that sometimes carries an
 * utterance is a status topic no consumer can trust. It is also not a REST route — a turn is
 * conversation traffic and belongs on the same data plane as the voice it substitutes for.
 */
export const CANDIDATE_TURN_TOPIC = 'skillfit.candidate_turn';
/*
 * ⭐ The recruiter's identity verdict, on its own topic, published by the
 * OBSERVER's browser and read by the candidate's.
 *
 * Why a data message at all: `identityOverridePending` reaches each browser on the join answer,
 * which is a SNAPSHOT nothing re-fetches. Without this the candidate would sit behind a disabled
 * Start button until they reloaded, with no reason to think reloading would help.
 *
 * ⭐ It is a HINT that unlocks a control, never the authority. `startInterview` refuses on the
 * session document itself, so a forged message buys the sender a button that still 409s.
 * ⛔ Not folded onto a `skillfit.*` status topic: this is neither a status nor conversation
 * traffic, and the topics above are asserted content-free by the worker suites.
 */
export const IDENTITY_OVERRIDE_TOPIC = 'skillfit.identity_override';
/*
 * The INBOUND transcript. LiveKit text streams publish transcription segments on this reserved
 * topic (`lk.transcription`), consumed via `room.registerTextStreamHandler(TRANSCRIPTION_TOPIC, …)`
 * — NOT via the `publishData`/`DataReceived` path the four topics above use. Both the agent's
 * speech and the candidate's own STT arrive here, which is why the transcript and the composer can
 * be two views of ONE `turns` array.
 */
export const TRANSCRIPTION_TOPIC = 'lk.transcription';

/*
 * Speak mode has no submit control: a spoken turn ends when the candidate falls silent.
 *
 * ⚠ PRODUCT-SIGNOFF CONSTANT, grouped with the other calibration constants. This value is a PLACEHOLDER awaiting sign-off,
 * NOT an approved policy number — it is named here so the eventual policy has one home instead of
 * a magic number sprinkled through the room, and so calibration can move it in one edit. No timer
 * is built around it in this duck; the duck holds the number, not the policy.
 */
export const SPEAK_TURN_SILENCE_THRESHOLD_MS = 2500;

/*
 * Server-gated interaction modes, carried on the `GET /ai-interview/join` body.
 *
 * ⭐ The candidate-input default is `speech_only` and that is load-bearing: typing is gated by the
 * server and must NEVER be unconditionally on. When the field is absent, unrecognised, or the join
 * body has not landed yet, the room falls back to Speak-only rather than offering a typing
 * affordance this interview may not permit.
 */
export const CANDIDATE_INPUT_MODES = ['speech_only', 'speech_and_text'];
export const AI_OUTPUT_MODES = ['speech_and_text', 'text_only'];
export const DEFAULT_CANDIDATE_INPUT_MODE = 'speech_only';
export const DEFAULT_AI_OUTPUT_MODE = 'speech_and_text';

// Device-health vocabulary for the room's status chips.
export const DEVICE_HEALTH_KEYS = ['camera', 'mic', 'network', 'screenShare'];
export const DEVICE_HEALTH_STATUSES = ['ok', 'degraded', 'failed', 'unknown'];

export const ROOM_THEME_PREFERENCES = ['light', 'dark'];
export const DEFAULT_ROOM_THEME_PREFERENCE = 'light';

// ================ Action types ================ //

export const ROOM_CONNECTED = 'app/AIInterviewRoomPage/ROOM_CONNECTED';
export const ROOM_DISCONNECTED = 'app/AIInterviewRoomPage/ROOM_DISCONNECTED';
/*
 * The reconnecting screen: the client is STILL TRYING. A distinct action from
 * `ROOM_DISCONNECTED`, which now means the narrower thing it always should have meant — the client
 * has GIVEN UP. The two are one drop seen at two moments, and the screens differ accordingly:
 * actively-retrying draws the reconnecting screen, given-up-and-held draws the paused screen.
 */
export const ROOM_RECONNECTING = 'app/AIInterviewRoomPage/ROOM_RECONNECTING';
export const SET_VOICE_PIPELINE_STATUS = 'app/AIInterviewRoomPage/SET_VOICE_PIPELINE_STATUS';
export const SET_CONDUCTING_STATUS = 'app/AIInterviewRoomPage/SET_CONDUCTING_STATUS';
export const SET_CONDUCTING_STATE = 'app/AIInterviewRoomPage/SET_CONDUCTING_STATE';
export const SET_TIME_BUDGET_STATUS = 'app/AIInterviewRoomPage/SET_TIME_BUDGET_STATUS';
export const APPEND_AGENT_TEXT = 'app/AIInterviewRoomPage/APPEND_AGENT_TEXT';
// The identity-gate -> screen-share -> room handoff (GET /join, two server hops).
export const JOIN_ROOM_REQUEST = 'app/AIInterviewRoomPage/JOIN_ROOM_REQUEST';
export const JOIN_ROOM_SUCCESS = 'app/AIInterviewRoomPage/JOIN_ROOM_SUCCESS';
export const JOIN_ROOM_ERROR = 'app/AIInterviewRoomPage/JOIN_ROOM_ERROR';
// The paused screen: the dropped-connection pause and the rejoin out of it.
export const RESUME_REQUEST = 'app/AIInterviewRoomPage/RESUME_REQUEST';
export const RESUME_SUCCESS = 'app/AIInterviewRoomPage/RESUME_SUCCESS';
export const RESUME_ERROR = 'app/AIInterviewRoomPage/RESUME_ERROR';

// The room's read of the three fields that only DESCRIBE the interview, on its own path with its own
// three actions. ⛔ Deliberately not folded into the join: the whole point is that this can fail, or
// never answer at all, without touching whether anyone is in the room.
export const ROOM_DISPLAY_REQUEST = 'app/AIInterviewRoomPage/ROOM_DISPLAY_REQUEST';
export const ROOM_DISPLAY_SUCCESS = 'app/AIInterviewRoomPage/ROOM_DISPLAY_SUCCESS';
export const ROOM_DISPLAY_ERROR = 'app/AIInterviewRoomPage/ROOM_DISPLAY_ERROR';
export const GRACE_WINDOW_EXPIRED = 'app/AIInterviewRoomPage/GRACE_WINDOW_EXPIRED';

/**
 * The candidate-facing mirror of the server's fixed grace window
 * (`sessionService.GRACE_WINDOW_MS`, 10 minutes — asserted there by
 * `sessionService.test.js:302`). It exists only to drive the advisory countdown: an offline client
 * cannot re-read the server's real deadline, and this number NEVER gates a rejoin — the
 * server's answer is the only authority on whether the window is still open.
 */
export const GRACE_WINDOW_MS = 10 * 60 * 1000;

/*
 * ⭐ THE ROOM'S OWN POLL — the answer to "what is this session actually doing?", asked repeatedly.
 *
 * The room learns everything from its join answer, which is a SNAPSHOT nothing re-fetches. Two
 * failures came out of that one gap: a Start pressed by one party left the OTHER party's
 * pre-interview banner up forever, and an interviewer that never arrived was never noticed at all.
 *
 * ⛔ The answer lands in ITS OWN fields below. `credentials` is the server's word AS GIVEN at join
 * time and is never rewritten — every other reader of that object depends on it meaning exactly
 * that.
 */
export const SESSION_STATUS_SUCCESS = 'app/AIInterviewRoomPage/SESSION_STATUS_SUCCESS';
export const SESSION_STATUS_ERROR = 'app/AIInterviewRoomPage/SESSION_STATUS_ERROR';
export const RETRY_INTERVIEWER_REQUEST = 'app/AIInterviewRoomPage/RETRY_INTERVIEWER_REQUEST';
export const RETRY_INTERVIEWER_SUCCESS = 'app/AIInterviewRoomPage/RETRY_INTERVIEWER_SUCCESS';
export const RETRY_INTERVIEWER_ERROR = 'app/AIInterviewRoomPage/RETRY_INTERVIEWER_ERROR';

/** Same cadence as the two waiting-screen polls, so the whole flow breathes at one rate. */
export const SESSION_STATUS_POLL_INTERVAL_MS = 5000;

/**
 * ⭐ How long a dispatched interviewer is presumed to still be ARRIVING, mirroring the server's own
 * re-dispatch cooldown — the codebase's existing statement of exactly this, and the same mirroring
 * discipline `GRACE_WINDOW_MS` above follows.
 *
 * ⛔ NOT the 2-minute initial-connection timeout: that is the candidate's own WebRTC handshake and
 * only ever fires on a session nobody entered. ⛔ NOT the 10-minute grace window: that is how long a
 * DISCONNECTED candidate may take to come back. Borrowing the first would accuse a healthy dispatch
 * of failing; borrowing the second would leave a candidate sitting in a silent, recording room for
 * ten minutes before anyone told them anything.
 *
 * It bounds the message AND the retry control, so the server's "your attempt is still arriving" can
 * never be a candidate's first feedback from pressing a button.
 */
export const INTERVIEWER_ARRIVAL_GRACE_MS = 30 * 1000;

/**
 * Statuses in which the interview has NOT begun, as the POLL reports them.
 *
 * ⚠ Deliberately expressed as "everything that is not pre-start and not over" rather than a second
 * list of running statuses: `in_progress` and `paused_disconnected` are both interviews under way,
 * and a list would have to be found and extended the next time a running status is added.
 */
const POLL_PRE_START_STATUSES = ['not_started', 'in_room'];

/**
 * The status written when a human observer records that the candidate's identity does not match.
 * The server ends the attempt on it and the status read carries it through untouched, so it is the
 * only thing either browser is ever told about that decision.
 */
export const SESSION_STATUS_IDENTITY_FAILED = 'terminated_identity_failed';

const POLL_TERMINAL_STATUSES = SESSION_END_STATES;

/**
 * ⭐ THE ONE STATEMENT OF "THIS INTERVIEW IS OVER", and the reason it is exported.
 *
 * The room carried its own copy of this list beside the poll's stop condition, and the two could
 * drift: widen one and not the other, and a closed interview goes on asking the server for updates
 * every five seconds for as long as its tab stays open. Everything that needs the answer reads this
 * predicate, so there is exactly one list to widen.
 *
 * ⛔ Unknown is NOT terminal — a room that has heard nothing has everything still to hear.
 */
export const isTerminalSessionStatus = sessionStatus =>
  !!sessionStatus && POLL_TERMINAL_STATUSES.includes(sessionStatus);

/**
 * Has the interview begun, as far as the SERVER is concerned?
 *
 * ⛔ Unknown reads as NOT begun. A room that has heard nothing must never take a running interview's
 * banner down on a guess — and every hermetic render that polls nothing must behave exactly as it
 * did before this existed.
 */
export const hasInterviewBegun = sessionStatus =>
  !!sessionStatus &&
  !POLL_PRE_START_STATUSES.includes(sessionStatus) &&
  !isTerminalSessionStatus(sessionStatus);

/**
 * ⭐ **HAS THE INTERVIEWER FAILED TO TURN UP?** Three facts, and all three are required.
 *
 *   1. the server says the interview has BEGUN — before that there is nothing to have arrived for;
 *   2. the ROOM'S OWN participant list shows no agent;
 *   3. more than the grace has passed since a dispatch was attempted.
 *
 * ⭐ **(2) IS ASKED OF THE ROOM, NEVER OF OUR DISPATCH RECORDS.** `interviewerDispatchedAt` records
 * an ATTEMPT and is stamped even on a transition that never reached a worker at all. Reading it as
 * arrival is precisely why a room with nobody in it looked healthy while the candidate was told the
 * interview was about to begin, indefinitely.
 *
 * ⛔ Every unknown reads as "say nothing": no status yet, no dispatch record, or a terminal session
 * all return false. The cost of a false positive here is telling a candidate their interview is
 * broken while it is working.
 */
export const isInterviewerMissing = ({
  sessionStatus,
  agentPresent,
  interviewerDispatchedAt,
  now = Date.now(),
} = {}) => {
  if (!hasInterviewBegun(sessionStatus)) return false;
  if (agentPresent) return false;
  if (!interviewerDispatchedAt) return false;
  const dispatchedMs = new Date(interviewerDispatchedAt).getTime();
  if (!Number.isFinite(dispatchedMs)) return false;
  return now - dispatchedMs > INTERVIEWER_ARRIVAL_GRACE_MS;
};

// The conversational turn model. `APPEND_TURN` is a NEW action beside `APPEND_AGENT_TEXT`,
// never a replacement for it — see the `turns` comment in the initial state.
export const APPEND_TURN = 'app/AIInterviewRoomPage/APPEND_TURN';
export const SET_COMPOSER_DRAFT = 'app/AIInterviewRoomPage/SET_COMPOSER_DRAFT';
export const SUBMIT_TURN_REQUEST = 'app/AIInterviewRoomPage/SUBMIT_TURN_REQUEST';
export const SUBMIT_TURN_SUCCESS = 'app/AIInterviewRoomPage/SUBMIT_TURN_SUCCESS';
export const SUBMIT_TURN_ERROR = 'app/AIInterviewRoomPage/SUBMIT_TURN_ERROR';
export const SET_ROOM_THEME_PREFERENCE = 'app/AIInterviewRoomPage/SET_ROOM_THEME_PREFERENCE';
export const SET_DEVICE_HEALTH = 'app/AIInterviewRoomPage/SET_DEVICE_HEALTH';
// The identity match came back verified while this room was open.
export const IDENTITY_OVERRIDE_RESOLVED = 'app/AIInterviewRoomPage/IDENTITY_OVERRIDE_RESOLVED';

// ================ Reducer ================ //

const initialState = {
  connected: false,
  /*
   * ⭐ The three fields that only DESCRIBE the interview — the candidate's name, the job title and
   * the employer — plus the job id, as the room's own read resolved them. `null` until that read
   * lands, which is what the top bar renders its em-dashes from.
   *
   * ⛔ NOT part of `credentials`, and never merged into it. The credential is what admits a party to
   * the room; these are what the screen says about the interview once they are in. They used to be
   * the same answer, and a slow lookup for these three cosmetic facts is what held a recruiter
   * outside an interview that ran without them.
   */
  roomDisplay: null,
  agentText: null, // latest agent intro/question text rendered from the worker's transcription
  // Two independent failure-signal states — never merged.
  voicePipelineStatus: null, // { status: 'degraded'|'recovered'|'unrecoverable', leg: 'stt'|'tts' }
  conductingStatus: null, // { status: 'degraded'|'recovered' }
  questioningActive: false, // drives the proctor view's mute-state label
  // Latest time-budget pacing snapshot (raw seconds + phase enum). Independent slice — never
  // merged with the failure signals above, so the two are never conflated.
  timeBudgetStatus: null,
  /*
   * ⭐ The recruiter's identity verdict, as this browser learned it AFTER joining.
   *
   * ⛔ NOT a copy of the join answer's `identityOverridePending`, and never initialised from it.
   * The join answer is the server's word at join time and stays where it is (`credentials`); this
   * is the one later event that can make it stale, and the room reads the gate as
   * `credentials.identityOverridePending && !identityOverrideResolved`.
   *
   * ⚠ One-way by construction: it only ever goes false -> true. A room whose gate has been cleared
   * cannot have it re-imposed by a second data message, which is what keeps a hostile publisher
   * from re-locking a candidate mid-interview.
   */
  identityOverrideResolved: false,
  // Handoff result. Named `credentials`, not `session`: `livekitToken` is a short-lived
  // (10m) participant token, not durable interview state. Shape is exactly the `GET /join` body —
  // { interviewId, roomName, livekitToken, livekitUrl, role }. `livekitUrl` travels here
  // rather than a build-time `REACT_APP_*` var: the browser needs both halves of
  // `Room.connect(url, token)`, and a second copy of the URL could drift from the `LIVEKIT_URL`
  // the server minted the token against. It is `null` when the server has no LIVEKIT_URL set.
  joinInProgress: false,
  joinError: null,
  credentials: null,
  // The paused screen — the dropped-connection pause. `pausedAt` is set ONLY on a live-room drop (a
  // `connected: true -> false` transition), never on the `connected: false` a cold page load starts
  // in: otherwise every candidate would open the interview already looking at the paused screen.
  pausedAt: null,
  graceWindowExpiresAt: null, // last-known deadline; advisory only
  resumeInProgress: false,
  resumeError: null,
  graceExpired: false, // terminal — set only by the SERVER's refusal
  /*
   * The reconnecting screen — is a rejoin IN FLIGHT? ONE field with two writers: the client's
   * own automatic retry raises it once `PAUSE_SETTLE_MS` elapses, and the explicit resume raises it
   * too. ⛔ Deliberately not `resumeInProgress`, which knows only the explicit path, and ⛔ not
   * container-local state — two differently-sourced notions of "not connected" are free to disagree,
   * which is precisely how an earlier stuck-spinner defect happened.
   *
   * ⚠ Cleared by `ROOM_CONNECTED` and by every terminal outcome below. Miss one and a recovered
   * candidate keeps a spinner over a live room.
   */
  reconnecting: false,
  /*
   * The signal `useContinuousVideoReverification` samples on after a reconnect. It carries the
   * `pausedAt` of the drop episode just recovered from, NOT the moment of recovery, and that choice
   * is load-bearing rather than incidental:
   *
   * Both writers below fire for one explicit rejoin — `RESUME_SUCCESS` lands first, then the room's
   * `ROOM_CONNECTED`. A fresh `Date.now()` in each would be two different values for ONE reconnect,
   * and the hook's guard (which dedupes on equality) would let both through, so the candidate would
   * be sampled twice for a single drop. The episode's own `pausedAt` is identical across that pair,
   * so the guard collapses them into the one sample a reconnect calls for — while still CHANGING on the next
   * drop, which is the property the hook actually needs.
   *
   * ⛔ Null on an ordinary first join: nothing was recovered, so nothing is sampled.
   */
  reconnectedAt: null,
  /*
   * THE conversation model. The transcript and the composer are two VIEWS of this one
   * array, not two features: what the candidate types is appended here exactly as what the agent
   * says is, and `selectCurrentQuestion` is derived from it rather than tracked separately.
   *
   * ⚠ Distinct from `agentText` above, which is left untouched: despite `APPEND_AGENT_TEXT`'s name
   * it REPLACES, and it feeds the container's `agentPresenceStateFor`. Turns are additive beside
   * it, never a rework of it.
   *
   * Each entry is `{ id, speaker: 'agent'|'candidate', text, at, final }`; `final: false` is an
   * in-flight interim transcription that a later segment with the SAME `id` upserts into place.
   * Ordered by arrival, never sorted.
   */
  turns: [],
  composerDraft: '', // survives a failed submit so the candidate can retry
  turnSubmitState: { submitting: false, error: null },
  roomThemePreference: DEFAULT_ROOM_THEME_PREFERENCE,
  // Sparse map of `{ camera, mic, network, screenShare }` -> 'ok'|'degraded'|'failed'|'unknown'.
  // Stored sparse and merged per device; `selectDeviceHealth` fills the gaps with 'unknown'.
  deviceHealth: {},
  /*
   * The room's own poll of the live session — ⛔ NOT a copy of anything on `credentials`, and never
   * initialised from it. The join answer says what was true at join time; these say what is true
   * now, and the room reads the two together.
   *
   * `null` means "we have not heard", which every reader must treat as "say nothing" rather than as
   * a default. A room that has not yet been answered behaves exactly as it did before this existed.
   */
  sessionStatus: null,
  interviewerDispatchedAt: null,
  /*
   * ⭐ The END-STATE DETAIL the same read carries — why the interview stopped, when it was paused,
   * the deadline the SERVER recorded for a return, and whether the lapsed-window sweep has run.
   *
   * ⛔ Kept in its own object rather than folded into `pausedAt`/`graceWindowExpiresAt` above,
   * which look like the same two facts and are not: those are THIS BROWSER'S OWN projection, written
   * when it noticed its own connection drop, and the reconnect edge is recognised by reading them.
   * Writing the server's values into them would change the meaning of a value the candidate's
   * reconnect logic depends on. The two never mix.
   *
   * Every field is `null` until the poll answers, and a `null` here means "nothing to report" —
   * never "we could not tell", so a reader never has to distinguish an absent key from an empty one.
   */
  sessionStatusDetail: {
    pausedAt: null,
    graceWindowExpiresAt: null,
    completionTrigger: null,
    graceWindowHandledAt: null,
    prepWindowExpiresAt: null,
    disconnectedParty: null,
    reInterviewOffered: false,
  },
  sessionStatusError: null,
  retryInterviewerInProgress: false,
  retryInterviewerOutcome: null,
};

/**
 * Upsert keyed on `id`: a segment whose id is already in the transcript REPLACES that entry IN
 * PLACE — an interim transcription becoming final must not appear as a second bubble, and must not
 * jump to the bottom of a transcript the candidate is already reading. A new id appends.
 */
const upsertTurn = (turns, incoming) => {
  const idx = turns.findIndex(t => t.id === incoming.id);
  if (idx === -1) return [...turns, incoming];
  const next = turns.slice();
  next[idx] = { ...next[idx], ...incoming };
  return next;
};

const isUsableTurn = t => !!t && typeof t.id === 'string' && t.id.length > 0;

// Stable fallback references for the selectors: a fresh `[]`/`{}` per call would give `connect` a
// new prop identity on every store tick and re-render the transcript for nothing.
const EMPTY_TURNS = [];
const EMPTY_SUBMIT_STATE = { submitting: false, error: null };

export default function aiInterviewRoomPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case ROOM_CONNECTED:
      // Back in the room: every trace of the pause is cleared, including a failed rejoin's error.
      // ⛔ `graceExpired` is deliberately cleared too — reaching a connected room after an expiry is
      // impossible via `resume()`, so if it ever happens the room is real and the terminal state is
      // the stale claim.
      return {
        ...state,
        connected: true,
        pausedAt: null,
        graceWindowExpiresAt: null,
        resumeInProgress: false,
        resumeError: null,
        graceExpired: false,
        /*
         * ⚠ The ONE thing this case does not clear, and the reason it is written here rather than in
         * a second case: this is the reconnect EDGE. `pausedAt` set means the room we just connected
         * to is one we had lost — the room's own silent retry reaches the reconnect sample through this line and no
         * other. Connecting without a pause is a first join and stays null.
         *
         * It holds the recovered episode rather than resetting, so a repeat `ROOM_CONNECTED` for the
         * same episode (pause already cleared) cannot re-fire the sample.
         */
        reconnectedAt: state.pausedAt || state.reconnectedAt,
        reconnecting: false, // the rejoin is over the moment the room is live again
      };
    case ROOM_RECONNECTING:
      /*
       * The still-retrying drop lands here. ⚠ It records the SAME pause episode `ROOM_DISCONNECTED` records, and
       * that is load-bearing rather than tidy: `reconnectedAt` above reads `pausedAt` to recognise
       * the reconnect edge, so a retry that recovered WITHOUT ever setting it would sail back into
       * the room unchecked — and a transient drop recovered by the client's own retry is exactly
       * the case the reconnecting screen exists for.
       *
       * ⛔ Nothing here is user-visible on its own: `pausedAt` is set either
       * way, so the held screen behaves precisely as it did before.
       */
      if (!state.connected && !state.pausedAt) return state; // no live connection was ever lost
      return {
        ...state,
        connected: false,
        reconnecting: true,
        // ⚠ First episode wins. A retry that re-raises mid-flight is the same drop, not a new one.
        pausedAt: state.pausedAt || (payload && payload.pausedAt) || null,
        graceWindowExpiresAt:
          state.graceWindowExpiresAt || (payload && payload.graceWindowExpiresAt) || null,
      };
    case ROOM_DISCONNECTED:
      // ⚠ Only a drop from a LIVE room is a pause. A `connected: false -> false` transition is the
      // cold-load state and the post-end teardown; treating either as a pause would put the paused
      // screen in front of a candidate who never had a connection to lose.
      //
      // ⭐ The reconnecting screen narrows what this action MEANS: the client has given up. It is the one signal
      // that lowers `reconnecting`, and lowering it is what lets the held screen take over from the
      // spinner (the render's ordering, expressed in state rather than in the render). ⚠ The `false` branch
      // is now the ordinary path OUT of a retry — `ROOM_RECONNECTING` already set `connected: false`
      // and recorded the episode, so the spread preserves the `pausedAt` the held screen needs.
      return state.connected
        ? {
            ...state,
            connected: false,
            reconnecting: false,
            pausedAt: (payload && payload.pausedAt) || null,
            graceWindowExpiresAt: (payload && payload.graceWindowExpiresAt) || null,
          }
        : { ...state, connected: false, reconnecting: false };
    case APPEND_AGENT_TEXT:
      return { ...state, agentText: payload };
    case SET_VOICE_PIPELINE_STATUS:
      // 'recovered' clears the degraded indicator => store null on recovered.
      return {
        ...state,
        voicePipelineStatus: payload && payload.status === 'recovered' ? null : payload,
      };
    case SET_CONDUCTING_STATUS:
      return {
        ...state,
        conductingStatus: payload && payload.status === 'recovered' ? null : payload,
      };
    case SET_CONDUCTING_STATE:
      return { ...state, questioningActive: !!(payload && payload.questioning_active) };
    case SET_TIME_BUDGET_STATUS:
      return { ...state, timeBudgetStatus: payload || null };
    // ⛔ No payload is read and no branch un-sets it: the action's existence IS the fact (see the
    // one-way note on the initial state).
    case IDENTITY_OVERRIDE_RESOLVED:
      return { ...state, identityOverrideResolved: true };
    case SESSION_STATUS_SUCCESS:
      /*
       * ⛔ `credentials` is deliberately absent from this spread. It is the join answer AS GIVEN, and
       * folding a later status into it would leave nothing able to say what the server originally
       * said — while silently changing what every other reader of that object sees.
       */
      return {
        ...state,
        sessionStatus: (payload && payload.status) || state.sessionStatus,
        interviewerDispatchedAt:
          payload && 'interviewerDispatchedAt' in payload
            ? payload.interviewerDispatchedAt
            : state.interviewerDispatchedAt,
        /*
         * ⛔ The ONLY writer of this slice — it says what the SERVER reports, so nothing else may
         * put a value in it. Each field falls back to `null` rather than to the previous answer: an
         * interview that has been resumed reports no pause moment, and keeping the last one would
         * leave a stale deadline counting down over a live room.
         */
        sessionStatusDetail: {
          pausedAt: (payload && payload.pausedAt) || null,
          graceWindowExpiresAt: (payload && payload.graceWindowExpiresAt) || null,
          completionTrigger: (payload && payload.completionTrigger) || null,
          graceWindowHandledAt: (payload && payload.graceWindowHandledAt) || null,
          /*
           * ⭐ WHEN the pre-interview window runs out. The join answer carries it only for a party
           * whose session was already in the room at that moment — which an observer arriving ahead
           * of the candidate, the intended order, never is — so without this the countdown they are
           * owed never started while the window ended their attempt on time.
           */
          prepWindowExpiresAt: (payload && payload.prepWindowExpiresAt) || null,
          /*
           * ⭐ WHICH PARTY a pause is for: `'candidate'`, `'interviewer'`, or `null`. A `null` is
           * read as the candidate's — a server that does not send the field, and every pause
           * recorded before it existed, can only have paused for them. ⛔ Falls back to `null`
           * like the rest, so an interviewer that has come back does not leave its drop standing
           * over the resumed interview.
           */
          disconnectedParty: (payload && payload.disconnectedParty) || null,
          /*
           * ⭐ Whether a re-interview has actually gone out for this interview. The endings promise
           * one only when this is `true`, so only an explicit `true` from the server counts.
           */
          reInterviewOffered: !!payload && payload.reInterviewOffered === true,
        },
        sessionStatusError: null,
      };
    case SESSION_STATUS_ERROR:
      /*
       * ⚠ The last known status SURVIVES a failed tick. Blanking it would drop the pre-interview
       * banner back over a running interview every time the network hiccupped, and would retract a
       * non-arrival message the moment the candidate most needs it.
       */
      return { ...state, sessionStatusError: payload || null };
    case ROOM_DISPLAY_REQUEST:
      return state;
    case ROOM_DISPLAY_SUCCESS:
      return { ...state, roomDisplay: payload || null };
    case ROOM_DISPLAY_ERROR:
      // ⛔ The fields are NOT cleared. A read that failed knows nothing an earlier one did not, and
      // blanking a name the recruiter is already reading would turn a retry into a flicker — a
      // watcher confirming they have the right person would see the answer withdrawn.
      return state;
    case RETRY_INTERVIEWER_REQUEST:
      return { ...state, retryInterviewerInProgress: true, retryInterviewerOutcome: null };
    case RETRY_INTERVIEWER_SUCCESS:
      // ⚠ `redispatched: false` is an ANSWER, not an error — "one is already there" and "your
      // attempt is still arriving" are both good news for whoever pressed.
      return {
        ...state,
        retryInterviewerInProgress: false,
        retryInterviewerOutcome: {
          redispatched: !!(payload && payload.redispatched),
          reason: (payload && payload.reason) || null,
        },
      };
    case RETRY_INTERVIEWER_ERROR:
      return {
        ...state,
        retryInterviewerInProgress: false,
        retryInterviewerOutcome: { redispatched: false, reason: null, error: payload || null },
      };
    case JOIN_ROOM_REQUEST:
      return { ...state, joinInProgress: true, joinError: null };
    case JOIN_ROOM_SUCCESS:
      return { ...state, joinInProgress: false, credentials: payload };
    case JOIN_ROOM_ERROR:
      return { ...state, joinInProgress: false, joinError: payload };
    case RESUME_REQUEST:
      // The explicit rejoin is the second writer. ⚠ It stays raised through
      // `RESUME_SUCCESS`: the candidate is not back until the room says so, and the caller re-enters
      // the room on success, so `ROOM_CONNECTED` is what lowers it.
      return { ...state, resumeInProgress: true, resumeError: null, reconnecting: true };
    case RESUME_SUCCESS:
      // ⚠ The refreshed credential REPLACES the stored one on every success — the server rotates on
      // use and invalidates the previous hash, so retrying a stale credential can only 401.
      // ⛔ A success WITHOUT a refreshed token (the already-resumed path below) must not wipe the
      // credential the browser still holds — that would turn a benign race into a dead rejoin.
      return {
        ...state,
        resumeInProgress: false,
        resumeError: null,
        credentials:
          payload && payload.reconnectToken
            ? { ...(state.credentials || {}), reconnectToken: payload.reconnectToken }
            : state.credentials,
        /*
         * The explicit-rejoin path into the reconnect sample. The already-resumed race resolves here WITHOUT a
         * following `ROOM_CONNECTED` — the room never dropped from the server's point of view — so
         * relying on that case alone would leave this path unsampled. When both do fire, they carry
         * the same episode value and the hook samples once. See the field's note above.
         */
        reconnectedAt: state.pausedAt || state.reconnectedAt,
      };
    case RESUME_ERROR:
      // Terminal outcome 1: the rejoin failed and the candidate is back on the held screen with
      // an error to act on. ⛔ Leaving the flag up would spin over a failure they can see.
      return { ...state, resumeInProgress: false, resumeError: payload, reconnecting: false };
    case GRACE_WINDOW_EXPIRED:
      // Terminal: the server has already created the `offer_re_interview` stub, so there is nothing
      // left for the candidate to retry.
      // Terminal outcome 2 — ⛔ the strongest case for lowering the flag: a spinner promising a
      // rejoin over a window the server has already closed is a lie about the candidate's options.
      return {
        ...state,
        resumeInProgress: false,
        resumeError: null,
        graceExpired: true,
        reconnecting: false,
      };
    case APPEND_TURN:
      // A malformed segment is dropped rather than poisoning the transcript with an un-upsertable
      // entry (an entry with no id could never be finalised).
      return isUsableTurn(payload) ? { ...state, turns: upsertTurn(state.turns, payload) } : state;
    case SET_COMPOSER_DRAFT:
      return { ...state, composerDraft: typeof payload === 'string' ? payload : '' };
    case SUBMIT_TURN_REQUEST:
      return { ...state, turnSubmitState: { submitting: true, error: null } };
    case SUBMIT_TURN_SUCCESS:
      // The turn is gone from the composer only once it is actually on the wire.
      return { ...state, composerDraft: '', turnSubmitState: { submitting: false, error: null } };
    case SUBMIT_TURN_ERROR:
      // ⛔ `composerDraft` is deliberately NOT cleared here. A failed send must leave the candidate
      // holding their words so they can retry; clearing would destroy an answer they just typed.
      return { ...state, turnSubmitState: { submitting: false, error: payload || null } };
    case SET_ROOM_THEME_PREFERENCE:
      return {
        ...state,
        roomThemePreference: ROOM_THEME_PREFERENCES.includes(payload)
          ? payload
          : DEFAULT_ROOM_THEME_PREFERENCE,
      };
    case SET_DEVICE_HEALTH:
      // MERGE per device: each probe reports only the device it knows about, so a mic report must
      // never wipe the camera's status.
      return payload && typeof payload === 'object'
        ? { ...state, deviceHealth: { ...state.deviceHealth, ...payload } }
        : state;
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const roomConnected = () => ({ type: ROOM_CONNECTED });

/**
 * The drop. Carries the pause clock in its payload rather than reading it inside the reducer, which
 * stays pure. `graceWindowExpiresAt` is the client's own projection of the server's fixed window —
 * the server sets the real deadline from the moment IT saw the disconnect, and the two can differ
 * by however long the client took to notice. That drift is why the countdown is advisory.
 */
export const roomDisconnected = (at = Date.now()) => ({
  type: ROOM_DISCONNECTED,
  payload: {
    pausedAt: new Date(at).toISOString(),
    graceWindowExpiresAt: new Date(at + GRACE_WINDOW_MS).toISOString(),
  },
});
/**
 * The same drop, seen while the client is still retrying (the reconnecting screen). Deliberately the SAME
 * payload shape as `roomDisconnected` above — it is one episode with one clock, and a second clock
 * started at give-up would hand the candidate a countdown that quietly restarted after the settle.
 *
 * ⚠ The container dispatches this once `PAUSE_SETTLE_MS` elapses, in place of going straight to the
 * held state; `roomDisconnected` then means only what its name says, the client gave up.
 */
export const roomReconnecting = (at = Date.now()) => ({
  type: ROOM_RECONNECTING,
  payload: {
    pausedAt: new Date(at).toISOString(),
    graceWindowExpiresAt: new Date(at + GRACE_WINDOW_MS).toISOString(),
  },
});
export const appendAgentText = text => ({ type: APPEND_AGENT_TEXT, payload: text });
export const setVoicePipelineStatus = payload => ({ type: SET_VOICE_PIPELINE_STATUS, payload });
export const setConductingStatus = payload => ({ type: SET_CONDUCTING_STATUS, payload });
export const setConductingState = payload => ({ type: SET_CONDUCTING_STATE, payload });
export const setTimeBudgetStatus = payload => ({ type: SET_TIME_BUDGET_STATUS, payload });
export const joinRoomRequest = () => ({ type: JOIN_ROOM_REQUEST });
export const joinRoomSuccess = payload => ({ type: JOIN_ROOM_SUCCESS, payload });
export const joinRoomError = e => ({ type: JOIN_ROOM_ERROR, error: true, payload: e });
export const resumeRequest = () => ({ type: RESUME_REQUEST });
export const resumeSuccess = payload => ({ type: RESUME_SUCCESS, payload });
export const resumeError = e => ({ type: RESUME_ERROR, error: true, payload: e });
export const graceWindowExpired = () => ({ type: GRACE_WINDOW_EXPIRED });
export const appendTurn = turn => ({ type: APPEND_TURN, payload: turn });
export const setComposerDraft = text => ({ type: SET_COMPOSER_DRAFT, payload: text });
export const submitTurnRequest = () => ({ type: SUBMIT_TURN_REQUEST });
export const submitTurnSuccess = () => ({ type: SUBMIT_TURN_SUCCESS });
export const submitTurnError = e => ({ type: SUBMIT_TURN_ERROR, error: true, payload: e });
export const setRoomThemePreference = pref => ({ type: SET_ROOM_THEME_PREFERENCE, payload: pref });
export const setDeviceHealth = payload => ({ type: SET_DEVICE_HEALTH, payload });
export const identityOverrideResolved = () => ({ type: IDENTITY_OVERRIDE_RESOLVED });

// ================ Selectors ================ //
//
// The seam. Consumers currently reach into `state.AIInterviewRoomPage.<key>` directly; these are
// the replacement. Each takes the WHOLE redux state and tolerates the slice being absent entirely
// (cold store, or a test rendering the room with a partial store), so no consumer needs a guard.

const slice = state => (state && state.AIInterviewRoomPage) || {};

/**
 * The three describing fields, or `null` while the room has not learned them.
 *
 * ⛔ `null` is not an error — it is "not yet", and it is the state the top bar's em-dashes exist for.
 * The room is fully usable in it, which is the entire reason the read was taken off the entry path.
 */
export const selectRoomDisplay = state => slice(state).roomDisplay || null;

export const selectConnected = state => !!slice(state).connected;
export const selectQuestioningActive = state => !!slice(state).questioningActive;
export const selectVoicePipelineStatus = state => slice(state).voicePipelineStatus || null;
export const selectConductingStatus = state => slice(state).conductingStatus || null;
export const selectTimeBudgetStatus = state => slice(state).timeBudgetStatus || null;
export const selectIdentityOverrideResolved = state => !!slice(state).identityOverrideResolved;
export const selectTurns = state => slice(state).turns || EMPTY_TURNS;
export const selectComposerDraft = state => slice(state).composerDraft || '';
export const selectTurnSubmitState = state => slice(state).turnSubmitState || EMPTY_SUBMIT_STATE;
export const selectRoomThemePreference = state => {
  const pref = slice(state).roomThemePreference;
  return ROOM_THEME_PREFERENCES.includes(pref) ? pref : DEFAULT_ROOM_THEME_PREFERENCE;
};

/**
 * Every device answers with a KNOWN status. The slice stores health sparsely (each probe reports
 * only its own device), so the selector is where the gaps become the explicit 'unknown' the UI can
 * render — a consumer should never have to distinguish `undefined` from "we have not checked yet".
 */
export const selectDeviceHealth = state => {
  const raw = slice(state).deviceHealth || {};
  return DEVICE_HEALTH_KEYS.reduce((acc, key) => {
    acc[key] = DEVICE_HEALTH_STATUSES.includes(raw[key]) ? raw[key] : 'unknown';
    return acc;
  }, {});
};

/**
 * The current question, DERIVED from `turns` — the text of the newest FINAL agent turn, else null.
 *
 * Why derived and not a state key of its own: the room is ONE conversation model, and the current
 * question simply IS the agent's latest utterance in it. A second, independently-maintained source
 * of truth could disagree with the transcript the candidate is reading on screen — the candidate
 * would see one question in the transcript and another in the question header, with nothing in the
 * system able to say which is right. Interim (`final: false`) agent turns are skipped so a
 * half-transcribed question is never presented as the question to answer.
 *
 * ⛔ NOT derived from `agentText`; that key belongs to the agent-presence strip and replaces rather
 * than accumulates.
 *
 * "Newest" means latest by ARRIVAL (array position), not by `at`: the array is arrival-ordered and
 * never sorted, and an interim->final upsert keeps its original position, so position is the one
 * ordering that stays stable under upsert.
 */
export const selectCurrentQuestion = state => {
  const turns = selectTurns(state);
  for (let i = turns.length - 1; i >= 0; i--) {
    const t = turns[i];
    if (t && t.speaker === 'agent' && t.final === true) return t.text || null;
  }
  return null;
};

/**
 * ⭐ Typing is SERVER-GATED. `speech_only` is the fail-safe: absent credentials (the join body has
 * not landed, or a proctor's held entry carries no modes), an unrecognised value, or a value from
 * the wrong enum all resolve to Speak-only. Never offer a typing affordance this interview may not
 * permit — the failure mode of a wrong "on" is a candidate answering in a channel the interview
 * does not accept, which is worse than a candidate who can only speak.
 */
export const selectCandidateInputMode = state => {
  const mode = (slice(state).credentials || {}).candidateInputMode;
  return CANDIDATE_INPUT_MODES.includes(mode) ? mode : DEFAULT_CANDIDATE_INPUT_MODE;
};

/**
 * The AI's output channel. Default `speech_and_text` — the richer of the two is the safe fallback
 * here (unlike input): showing the agent's words as well as speaking them can never exceed what
 * the interview permits, and it is the accessible option.
 */
export const selectAiOutputMode = state => {
  const mode = (slice(state).credentials || {}).aiOutputMode;
  return AI_OUTPUT_MODES.includes(mode) ? mode : DEFAULT_AI_OUTPUT_MODE;
};

// ================ Thunks ================ //

/**
 * Redeem the candidate's `interviewToken` for this interview's room credentials.
 *
 * One client call spanning two server hops: `GET /api/skillfitInterviewHandoff` mints a single-use
 * handoff token and 302s to `GET /api/ai-interview/join`, which `fetch` follows automatically (see
 * `joinSkillFitInterviewRoom`). Resolves with `{ interviewId, roomName, livekitToken, role }`.
 *
 * DELIBERATELY re-callable, and dispatched twice in the screen-share -> room flow: once on identity-pass
 * (the screen-share gate needs `interviewId`) and again at room entry (for a fresh token). Both
 * hops are idempotent under repeat calls — hop 1 mints a NEW handoff token each time, so the
 * single-use constraint binds a token the client never holds, and `sessionService.getOrCreate`
 * matches on `interviewToken`, so a second call skips session creation, question pre-generation and
 * the skillFitChecks link entirely, returning the same `interviewId`.
 *
 * That re-callability is what makes the 10-minute participant-token TTL
 * (`liveKitRoomService.PARTICIPANT_TOKEN_TTL`) safe: a candidate who lingers on the screen-share
 * gate gets a freshly minted token at room entry rather than an expired one, with no separate
 * re-mint endpoint.
 */
/**
 * ONE TICK of the room's session poll.
 *
 * ⛔ Resolves rather than rejecting, always. It runs on an interval that nobody awaits, so a rejected
 * promise here is an unhandled rejection every five seconds for the length of an interview — and the
 * caller has no different course of action to take. The failure is fully represented in state, and
 * the last known status stays on screen.
 *
 * ⭐ It also HANDS THE ANSWER BACK, and that is not a convenience. Most callers are the interval and
 * ignore it, but a caller that asks OUT OF BAND — because something just happened that it must not
 * decide on a several-seconds-old value — needs this tick's own answer, not whatever the store holds
 * once React has got round to re-rendering it.
 *
 * ⛔ A failed tick resolves with nothing. "No answer" must never read as any particular status.
 */
/**
 * Ask the server what this interview is CALLED, once the party asking is already in the room.
 *
 * ⭐ Fire-and-forget by construction: it resolves whatever happens, and a rejection is recorded and
 * dropped. Nothing downstream may await it — the moment something does, a slow marketplace is back
 * on a path that can cost somebody their interview, which is the exact defect this split removed.
 *
 * ⛔ Never put this on a timer. Two live marketplace lookups for three facts that do not change while
 * an interview runs is a once-per-room cost, and it was that cost, on a hot path, that locked a
 * recruiter out for a whole sitting.
 */
export const fetchRoomDisplay = interviewToken => dispatch => {
  // ⛔ A read with no token is not a read of "this interview" — it is a 404 the room would then have
  // to explain. Say nothing instead; the dashes are already the honest answer.
  if (!interviewToken) return Promise.resolve();
  dispatch({ type: ROOM_DISPLAY_REQUEST });
  return fetchSkillFitInterviewRoomDisplay(interviewToken)
    .then(fields => {
      dispatch({ type: ROOM_DISPLAY_SUCCESS, payload: fields || null });
    })
    .catch(e => {
      dispatch({ type: ROOM_DISPLAY_ERROR, payload: storableError(e) });
    });
};

export const pollSessionStatus = ({ interviewToken, role } = {}) => dispatch => {
  if (!interviewToken) return Promise.resolve();
  return fetchSkillFitSessionStatus({ interviewToken, role })
    .then(response => {
      const answer = response || {};
      dispatch({ type: SESSION_STATUS_SUCCESS, payload: answer });
      return answer;
    })
    .catch(e => {
      dispatch({ type: SESSION_STATUS_ERROR, payload: storableError(e) });
    });
};

/**
 * ⭐ **ASK FOR ANOTHER INTERVIEWER** — the one thing the room may ask FOR rather than about.
 *
 * ⛔ Every guard that keeps a retry from putting a SECOND interviewer into the room lives on the
 * server, and none of them is duplicated here: two interviewers talk over each other and over the
 * candidate, into ONE transcript that is then graded as the candidate's answers. The client's only
 * job is to not offer the control before the grace has passed, so "your attempt is still arriving"
 * is never somebody's first feedback from pressing a button.
 *
 * Resolves rather than rejecting, for the same reason the poll does: the outcome is fully
 * represented in state and there is no other course of action to take.
 */
export const retryInterviewer = ({ interviewToken, role } = {}) => dispatch => {
  if (!interviewToken) return Promise.resolve();
  dispatch({ type: RETRY_INTERVIEWER_REQUEST });
  return retrySkillFitInterviewer({ interviewToken, role })
    .then(response => {
      dispatch({ type: RETRY_INTERVIEWER_SUCCESS, payload: response || {} });
    })
    .catch(e => {
      dispatch({ type: RETRY_INTERVIEWER_ERROR, payload: storableError(e) });
    });
};

export const joinInterviewRoom = interviewToken => dispatch => {
  dispatch(joinRoomRequest());
  return joinSkillFitInterviewRoom(interviewToken)
    .then(response => {
      dispatch(joinRoomSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(joinRoomError(storableError(e)));
      throw e;
    });
};

/**
 * The rejoin out of a dropped-connection pause.
 *
 * `POST /api/ai-interview/sessions/:interviewId/reconnect`, which flips the session back from
 * `paused_disconnected` to `in_progress`, increments `reconnectCount` and rotates the credential.
 * The route is mounted WITHOUT `requireAuth` on purpose: the session-scoped reconnect credential
 * IS the authentication.
 *
 * ⛔ `interviewId` comes from the stored `credentials` — the server's own join answer — and NEVER
 * from anything the candidate can supply. Resuming is a session-scoped action, so an id the browser
 * could name would be an id the browser could change.
 *
 * ⛔ This resolves the SERVER side of the rejoin only. The LiveKit participant token is 10-minute
 * TTL and the one in hand is stale by definition here, so the caller re-runs the (deliberately
 * re-callable) `joinInterviewRoom` afterwards to get a fresh one and let the room rebuild.
 *
 * Failure mapping: only the server decides that the window is closed.
 */
export const resumeInterview = () => (dispatch, getState) => {
  const slice = (getState() && getState().AIInterviewRoomPage) || {};
  const credentials = slice.credentials || {};

  dispatch(resumeRequest());
  return resumeSkillFitInterview({
    interviewId: credentials.interviewId,
    token: credentials.reconnectToken,
  })
    .then(response => {
      dispatch(resumeSuccess(response || {}));
      return { resumed: true, status: response && response.status };
    })
    .catch(e => {
      // The grace window closed while the candidate was away. Terminal — the server has already
      // created the `offer_re_interview` stub, so there is nothing left to retry.
      //
      // ⚠ `resumed: false` is load-bearing, not decoration: the caller re-enters the room on a
      // resume, and an expired session is still joinable at the LiveKit layer. Re-entering here
      // would reconnect a room whose session the server has already written off — and clear this
      // very terminal state on the way in.
      if (e && e.code === 'GRACE_WINDOW_EXPIRED') {
        dispatch(graceWindowExpired());
        return { resumed: false, expired: true };
      }
      // Not paused any more: something already resumed this session (a second tab, or a rejoin whose
      // response never made it back). That is the outcome the candidate wanted, so it is treated as
      // a success and the room is re-entered rather than reported as a failure.
      if (e && e.code === 'SESSION_NOT_PAUSED') {
        dispatch(resumeSuccess({}));
        return { resumed: true, alreadyResumed: true };
      }
      dispatch(resumeError(storableError(e)));
      throw e;
    });
};

/**
 * Pure mapping from an inbound LiveKit data-message (topic + parsed JSON payload) to the ONE action
 * it should dispatch. Kept pure and exported so the two-subscriptions-never-conflated requirement
 * is unit-testable without a live LiveKit room. Returns `null` for unknown topics.
 */
export const reduceDataMessage = (topic, payload) => {
  switch (topic) {
    case VOICE_PIPELINE_TOPIC:
      return setVoicePipelineStatus(payload);
    case CONDUCTING_STATUS_TOPIC:
      return setConductingStatus(payload);
    case CONDUCTING_STATE_TOPIC:
      return setConductingState(payload);
    case TIME_BUDGET_TOPIC:
      return setTimeBudgetStatus(payload);
    /*
     * ⭐ ONLY `manually_verified` unlocks anything. `does_not_match` ends the attempt on the
     * SERVER — the session is terminated there — so a client that acted on it would be racing the
     * termination to draw a screen it has no authority to draw, and any other payload is noise.
     * ⛔ Never `return identityOverrideResolved()` unconditionally: this topic is publishable by
     * anyone in the room, and the whole safety of a hint is that it can only ever say the one thing.
     */
    case IDENTITY_OVERRIDE_TOPIC:
      return payload && payload.result === 'manually_verified' ? identityOverrideResolved() : null;
    default:
      return null;
  }
};

/**
 * Pure mapping from ONE inbound LiveKit transcription segment to the `appendTurn` action it should
 * dispatch, or `null` when the segment is unusable. Same convention as `reduceDataMessage` above,
 * and for the same reason: the `registerTextStreamHandler(TRANSCRIPTION_TOPIC, …)` wiring becomes
 * unit-testable with no live LiveKit room.
 *
 * HOW `speaker` IS RESOLVED — and why it is an INPUT here, not something this function works out.
 * LiveKit hands the text-stream handler a `TextStreamReader` plus the sending `participantInfo`;
 * the room resolves that identity to a lane exactly once, at the call site:
 *
 *   identity === room.localParticipant.identity  ->  'candidate'   (the candidate's own STT)
 *   anything else                                ->  'agent'       (the sole remote publisher)
 *
 * Keeping that resolution at the call site is what keeps this mapper pure — it needs no `Room`
 * handle and no knowledge of who is local. The segment `id` is LiveKit's own segment id, which is
 * what makes the interim -> final upsert work: the same utterance keeps one id as it is revised.
 *
 * An unrecognised speaker returns null rather than defaulting to a lane. Guessing 'agent' for a
 * candidate's words would put the candidate's own speech into the question header via
 * `selectCurrentQuestion`; dropping a segment is recoverable (the final segment follows), silently
 * mislabelling one is not.
 */
export const reduceTranscriptionSegment = segment => {
  if (!segment) return null;
  const { id, text, speaker, at, final } = segment;
  if (typeof id !== 'string' || id.length === 0) return null;
  if (typeof text !== 'string' || text.trim().length === 0) return null;
  if (speaker !== 'agent' && speaker !== 'candidate') return null;
  return appendTurn({
    id,
    speaker,
    text,
    at: typeof at === 'number' ? at : Date.now(),
    // Absent `final` means an in-flight interim segment: LiveKit only asserts finality explicitly.
    final: final === true,
  });
};

// Monotonic suffix so two turns submitted inside the same millisecond still get distinct ids (the
// upsert is keyed on id — a collision would silently overwrite the previous turn).
let localTurnSeq = 0;

/**
 * UTF-8 bytes for `publishData`.
 *
 * `TextEncoder` is standard in browsers but is NOT on the global in this project's jsdom test
 * environment, so the fallback is a real UTF-8 encoder rather than a latin1 shortcut — a candidate
 * typing "Résumé" or an emoji must produce the same bytes on both paths, and a `charCodeAt & 0xff`
 * fallback silently corrupts every non-ASCII turn. `Buffer` is deliberately not used: it is a node
 * global, not a browser one.
 */
const utf8Bytes = str => {
  const out = [];
  for (let i = 0; i < str.length; i++) {
    const cp = str.codePointAt(i);
    if (cp > 0xffff) i++; // a surrogate pair was consumed as one code point
    if (cp < 0x80) {
      out.push(cp);
    } else if (cp < 0x800) {
      out.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
    } else if (cp < 0x10000) {
      out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    } else {
      out.push(
        0xf0 | (cp >> 18),
        0x80 | ((cp >> 12) & 0x3f),
        0x80 | ((cp >> 6) & 0x3f),
        0x80 | (cp & 0x3f)
      );
    }
  }
  return new Uint8Array(out);
};

const encodeJsonBytes = value => {
  const json = JSON.stringify(value);
  return typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(json) : utf8Bytes(json);
};

const isPublishableRoom = room =>
  !!room &&
  room.state !== 'disconnected' &&
  !!room.localParticipant &&
  typeof room.localParticipant.publishData === 'function';

/**
 * Send the candidate's typed turn over the LiveKit data plane.
 *
 * The `room` is INJECTED rather than held in the store: a `Room` is a live, non-serialisable object
 * with subscriptions and timers, and redux state must stay serialisable — so the container that
 * owns the connection hands it in per call. That injection is also what makes this thunk testable
 * with a plain fake object and no LiveKit at all.
 *
 * The optimistic `appendTurn` is what makes the composer and transcript one model: the candidate
 * sees their own words in the transcript the instant they send, in the same lane the agent's words
 * arrive in, rather than watching a spinner where their sentence should be.
 *
 * ⛔ No confirmation gate, no revise-in-place, no pending state. A turn is a conversation turn —
 * irreversible the moment it is sent, exactly like speaking. The composer stays live afterwards.
 */
/**
 * ⭐ The OBSERVER's half: tell the room the identity match came back verified.
 *
 * Two dispatches for one event, and both are needed. The publish is what reaches the CANDIDATE's
 * browser (a data message does not loop back to its sender); the local dispatch is what clears this
 * recruiter's OWN gate — their join answer said `identityOverridePending` too, and after the
 * decision their Start control must stop refusing for a reason that is no longer true.
 *
 * ⛔ Called only for `manually_verified`. `does_not_match` terminates the session server-side and
 * unlocks nothing.
 *
 * ⚠ The local dispatch runs FIRST and is not conditional on the publish. A data message that fails
 * to send leaves the candidate waiting for a control the recruiter can press themselves, which
 * is a degraded room; making the recruiter's own gate wait on the network as well would be a stuck
 * one. Failure is swallowed for the same reason `submitCandidateTurn` resolves rather than throws:
 * the server is already the authority, and no caller here has another course of action.
 */
export const publishIdentityOverrideResolved = ({ room } = {}) => dispatch => {
  dispatch(identityOverrideResolved());

  if (!isPublishableRoom(room)) return Promise.resolve();

  return Promise.resolve()
    .then(() =>
      room.localParticipant.publishData(encodeJsonBytes({ result: 'manually_verified' }), {
        reliable: true,
        topic: IDENTITY_OVERRIDE_TOPIC,
      })
    )
    .catch(() => {});
};

export const submitCandidateTurn = ({ room, text, beforeStart = false } = {}) => dispatch => {
  // Before Start no interviewer is in the room, so a turn shown as sent would reach nobody. Refused
  // without a dispatch: no error to show, and the draft stays in the composer for when it starts.
  if (beforeStart) return Promise.resolve();

  const trimmed = typeof text === 'string' ? text.trim() : '';
  // A blank draft is not a failure — it is nothing. No turn, no request, no error banner.
  if (trimmed.length === 0) return Promise.resolve();

  dispatch(submitTurnRequest());

  // Checked BEFORE the optimistic append: with no usable room nothing can reach the agent, and an
  // optimistically-appended turn would sit in the transcript as a message that was never sent.
  if (!isPublishableRoom(room)) {
    dispatch(submitTurnError(storableError(new Error('Interview room is not connected.'))));
    return Promise.resolve();
  }

  const at = Date.now();
  const id = `candidate-local-${at}-${++localTurnSeq}`;
  const turn = { id, speaker: 'candidate', text: trimmed, at, final: true };
  dispatch(appendTurn(turn));

  return Promise.resolve()
    .then(() =>
      room.localParticipant.publishData(encodeJsonBytes(turn), {
        reliable: true,
        topic: CANDIDATE_TURN_TOPIC,
      })
    )
    .then(() => {
      dispatch(submitTurnSuccess());
    })
    .catch(e => {
      // Resolved, not re-thrown: the failure is fully represented in `turnSubmitState`, the draft
      // is still in the composer, and no caller has a different course of action to take.
      dispatch(submitTurnError(storableError(e)));
    });
};
